import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { fijiTime } from "../lib/format";

type Link = { token: string; label: string | null; created_at: string; expires_at: string };
const DURATIONS: [number, string][] = [[1, "1 hour"], [4, "4 hours"], [24, "24 hours"], [72, "3 days"]];

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const r = await fetch(`/api/share${path}`, {
    method, headers: { Authorization: `Bearer ${data.session?.access_token ?? ""}`, ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const res = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(res.error ?? `Failed (${r.status})`);
  return res as T;
}

export const shareUrl = (token: string) => `${window.location.origin}/s/${token}`;

/** Small popover in the map's detail strip: create, copy and switch off live-location links. */
export default function ShareDialog({ deviceId, deviceName, onClose }: { deviceId: string; deviceName: string; onClose: () => void }) {
  const [hours, setHours] = useState(4);
  const [label, setLabel] = useState("");
  const [links, setLinks] = useState<Link[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(() => api<Link[]>("GET", `?device=${deviceId}`).then(setLinks).catch((e) => setErr(e.message)), [deviceId]);
  useEffect(() => { load(); }, [load]);

  async function create() {
    setBusy(true); setErr(null);
    try {
      const r = await api<{ token: string }>("POST", "", { device: deviceId, hours, label: label.trim() || undefined });
      await copy(r.token);
      setLabel("");
      await load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  async function copy(token: string) {
    try { await navigator.clipboard.writeText(shareUrl(token)); setCopied(token); setTimeout(() => setCopied(null), 2500); } catch { /* clipboard blocked */ }
  }
  async function revoke(token: string) {
    if (!confirm("Switch this link off now? Anyone using it will lose access.")) return;
    setBusy(true);
    try { await api("DELETE", `/${token}`); await load(); } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="share-pop" role="dialog" aria-label="Share live location">
      <div className="strip-head"><strong>Share live location</strong><button className="x" onClick={onClose} aria-label="Close">×</button></div>
      <p className="sub">Anyone with the link can see <b>{deviceName}</b>'s current position — no login, no history — until it expires.</p>
      <div className="chips">
        {DURATIONS.map(([h, t]) => <button key={h} className={hours === h ? "on" : ""} onClick={() => setHours(h)}>{t}</button>)}
      </div>
      <input className="asearch" placeholder="Name shown to the viewer (optional)" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} />
      <button className="btn-primary" onClick={create} disabled={busy}>{busy ? "Creating…" : "Create link & copy"}</button>
      {err && <div className="error">{err}</div>}
      {links.length > 0 && (
        <ul className="share-links">
          {links.map((l) => (
            <li key={l.token}>
              <div>
                <span className="mono">…/s/{l.token.slice(0, 8)}…</span>
                <div className="sub">{l.label ? `"${l.label}" · ` : ""}until {fijiTime(l.expires_at)}</div>
              </div>
              <button className="btn-ghost small" onClick={() => copy(l.token)}>{copied === l.token ? "Copied" : "Copy"}</button>
              <button className="btn-ghost small danger" onClick={() => revoke(l.token)} disabled={busy}>Stop</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
